import React from "react";
import type { LogoutIntent } from "./ssoSession";

/**
 * Where an authenticated user lands when no post-login deep link is stored.
 * Shared by AuthPage and AuthSAML: the SAML route defaulted to /findings from
 * 2021 until this deliberate alignment on one post-login destination.
 */
export const DEFAULT_AUTHENTICATED_TARGET = "/dashboard";

export const CONTEXT_KEY = "context";
export const ORGANIZATION_KEY = "organization";
export const ACCOUNT_KEY = "account";

/**
 * Authenticated Cognito user. The object stored in auth state is the raw
 * aws-amplify `CognitoUser`, augmented at runtime with the session/attributes
 * that `AuthProvider.populateAuthentication` reads. Only the fields actually
 * accessed across the app are typed here.
 */
export interface AuthUser {
  username: string;
  challengeName?: string;
  attributes?: {
    email?: string;
    [key: string]: unknown;
  };
  signInUserSession?: {
    accessToken?: { payload?: { sub?: string; [key: string]: unknown } };
    idToken?: {
      payload?: {
        email?: string;
        // `providerName` is the user's own Cognito IdP name — the same
        // string as the /saml/<param> route segment.
        identities?: Array<{
          userId: string;
          providerName?: string;
          [key: string]: unknown;
        }>;
        [key: string]: unknown;
      };
    };
  };
  // TODO: this is the aws-amplify CognitoUser; remaining members are left
  // untyped because consumers only read the fields declared above.
  [key: string]: unknown;
}

export interface AuthAccount {
  id: string;
  name: string;
}

export interface AuthOrganization {
  slug: string;
  name: string;
  domains?: string[];
  identity_provider?: string | null;
  password_enabled?: boolean;
  accounts: AuthAccount[];
}

/**
 * Impersonation / account-scoping context. Persisted in localStorage under
 * CONTEXT_KEY and used by the API clients to route requests and set the
 * `X-Account` header.
 */
export interface ImpersonationContext {
  id?: string;
  mainDomain?: string;
  customerName?: string;
  [key: string]: unknown;
}

export interface AuthState {
  user?: AuthUser | null;
  status?: string | null;
  username?: string;
  domain?: string;
  hashedUserId?: string;
  mfaType?: string | null;
  organization?: AuthOrganization;
  account?: AuthAccount;
  authenticated?: boolean;
  [CONTEXT_KEY]?: ImpersonationContext;
}

/**
 * Full value provided by {@link AuthContext}. Owns the shared auth shapes for
 * the auth module — sibling files import these types from here.
 */
export interface AuthContextType {
  authState: AuthState;
  organizations?: AuthOrganization[];
  updateAuthState: (state: Partial<AuthState>) => void;
  chooseOrganizationAndAccount: (
    organization: AuthOrganization,
    account: AuthAccount
  ) => void;
  populateAuthentication: (
    cognitoUser: AuthUser,
    mfaType?: string | null
  ) => Promise<void>;
  clearOrgAndAccount: () => void;
  stopImpersonation: () => void;
  logout: (options?: LogoutIntent) => void;
  isImpersonating: boolean;
}

export const LOGGED_OUT_STATE: AuthState = {
  user: null,
  status: null,
  username: undefined,
  domain: "",
  // Runtime value is the empty string (logged-out sentinel); the cast keeps the
  // public AuthState.context object-shaped for consumers that read context?.id.
  [CONTEXT_KEY]: "" as unknown as ImpersonationContext,
  organization: undefined,
  account: undefined,
  authenticated: false,
};

export const AuthContext = React.createContext<AuthContextType>({
  authState: { ...LOGGED_OUT_STATE },
  organizations: undefined,
  updateAuthState: () => {},
  chooseOrganizationAndAccount: () => {},
  populateAuthentication: () => Promise.resolve(),
  clearOrgAndAccount: () => {},
  stopImpersonation: () => {},
  logout: () => {},
  isImpersonating: false,
});
